import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-dive-black py-6 pb-24" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-dive-neon" />
            <h1 className="text-3xl font-display text-white tracking-wide">Privacy Policy</h1>
          </div>
          
          <p className="text-gray-400 text-sm">Last updated: March 16, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-sm max-w-none">
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-6 mb-6">
            <p className="text-gray-300 leading-relaxed">
              We respect your privacy and are committed to protecting it through our compliance with this privacy policy ("Policy"). This Policy describes the types of information we may collect from you or that you may provide ("Personal Information") on the{' '}
              <a href="https://www.gettilted.app" className="text-dive-neon hover:underline">gettilted.app</a>{' '}
              website ("Website" or "Service") and any of its related products and services (collectively, "Services"), and our practices for collecting, using, maintaining, protecting, and disclosing that Personal Information. It also describes the choices available to you regarding our use of your Personal Information and how you can access and update it.
            </p>
            
            <p className="text-gray-300 leading-relaxed mt-4">
              This Policy is a legally binding agreement between you ("User", "you" or "your") and this Website operator ("Operator", "we", "us" or "our"). If you are entering into this Policy on behalf of a business or other legal entity, you represent that you have the authority to bind such entity to this Policy, in which case the terms "User", "you" or "your" shall refer to such entity. If you do not have such authority, or if you do not agree with the terms of this Policy, you must not accept this Policy and may not access and use the Website and Services. By accessing and using the Website and Services, you acknowledge that you have read, understood, and agree to be bound by the terms of this Policy. This Policy does not apply to the practices of companies that we do not own or control, or to individuals that we do not employ or manage.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-dive-darker border border-dive-gray rounded-xl p-6 mb-6">
            <h3 className="text-white font-display text-lg mb-4">Table of Contents</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-400">
              <li><a href="#collection-of-personal-information" className="text-dive-neon hover:underline">Collection of personal information</a></li>
              <li><a href="#location-data" className="text-dive-neon hover:underline">Location data</a></li>
              <li><a href="#use-and-processing-of-collected-information" className="text-dive-neon hover:underline">Use and processing of collected information</a></li>
              <li><a href="#managing-information" className="text-dive-neon hover:underline">Managing information</a></li>
              <li><a href="#disclosure-of-information" className="text-dive-neon hover:underline">Disclosure of information</a></li>
              <li><a href="#retention-of-information" className="text-dive-neon hover:underline">Retention of information</a></li>
              <li><a href="#local-storage-and-cookies" className="text-dive-neon hover:underline">Local storage and cookies</a></li>
              <li><a href="#first-party-analytics" className="text-dive-neon hover:underline">Analytics</a></li>
              <li><a href="#age-restriction" className="text-dive-neon hover:underline">Age restriction (21+)</a></li>
              <li><a href="#do-not-track-signals" className="text-dive-neon hover:underline">Do Not Track signals</a></li>
              <li><a href="#links-to-other-resources" className="text-dive-neon hover:underline">Links to other resources</a></li>
              <li><a href="#information-security" className="text-dive-neon hover:underline">Information security</a></li>
              <li><a href="#data-breach" className="text-dive-neon hover:underline">Data breach</a></li>
              <li><a href="#changes-and-amendments" className="text-dive-neon hover:underline">Changes and amendments</a></li>
              <li><a href="#acceptance-of-this-policy" className="text-dive-neon hover:underline">Acceptance of this policy</a></li>
              <li><a href="#contacting-us" className="text-dive-neon hover:underline">Contacting us</a></li>
            </ol>
          </div>

          {/* Collection of Personal Information */}
          <section id="collection-of-personal-information" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Collection of Personal Information</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              You can browse our Website without creating an account. However, to use features such as rating bars, creating lists, or participating in the social feed, you must create an account and provide certain Personal Information.
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We receive and store any information you knowingly provide to us when you create an account, publish content, or fill any forms on the Website. When required, this information may include the following:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li><strong className="text-white">Account details:</strong> Username, unique user ID, password, and email address</li>
              <li><strong className="text-white">Date of birth:</strong> Used to verify you are 21 years of age or older (required for account creation)</li>
              <li><strong className="text-white">Profile information:</strong> Display name, profile photo, and bio (optional)</li>
              <li><strong className="text-white">User-generated content:</strong> Bar ratings, reviews, photos, comments, and lists you create</li>
              <li><strong className="text-white">Social connection data:</strong> Your followers, who you follow, and friend connections used to power your personalized Social Feed</li>
              <li><strong className="text-white">Third-party authentication:</strong> If you sign in with Google, we receive your name, email, and profile photo from Google</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed">
              You can choose not to provide us with your Personal Information, but then you may not be able to take advantage of some of the features on the Website. Users who are uncertain about what information is mandatory are welcome to contact us.
            </p>
          </section>

          {/* Location Data */}
          <section id="location-data" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Location Data</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              Tilted may request access to your device's precise geolocation (accurate to approximately 1,850 feet / 500 meters) to provide location-based features.
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">When we collect location data:</strong>
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>When you view the Map to show bars near you</li>
              <li>When you use the Bar Scanner feature to check in at a location</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">Important:</strong> We do <strong>not</strong> track your location in the background. Location is only accessed when you are actively using the Map or Bar Scanner features within the app. Your location data is used solely to display nearby bars and is not stored on our servers or shared with third parties.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              You can deny or revoke location permissions at any time through your device or browser settings. The app will continue to function, but location-based features will be unavailable.
            </p>
          </section>

          {/* Use and Processing */}
          <section id="use-and-processing-of-collected-information" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Use and Processing of Collected Information</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We act as a data controller and a data processor when handling Personal Information, unless we have entered into a data processing agreement with you in which case you would be the data controller and we would be the data processor.
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              In order to make the Website and Services available to you, or to meet a legal obligation, we may need to collect and use certain Personal Information. If you do not provide the information that we request, we may not be able to provide you with the requested products or services. Any of the information we collect from you may be used for the following purposes:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Create and manage user accounts</li>
              <li>Verify that you are 21 years of age or older</li>
              <li>Display personalized content in your Social Feed based on who you follow</li>
              <li>Send product and service updates</li>
              <li>Respond to inquiries and offer support</li>
              <li>Request user feedback</li>
              <li>Improve user experience</li>
              <li>Enforce terms and conditions and policies</li>
              <li>Protect from abuse and malicious users</li>
              <li>Respond to legal requests and prevent harm</li>
              <li>Run and operate the Website and Services</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed">
              Processing your Personal Information depends on how you interact with the Website and Services, where you are located in the world and if one of the following applies: (a) you have given your consent for one or more specific purposes; (b) provision of information is necessary for the performance of this Policy with you and/or for any pre-contractual obligations thereof; (c) processing is necessary for compliance with a legal obligation to which you are subject; (d) processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in us; (e) processing is necessary for the purposes of the legitimate interests pursued by us or by a third party.
            </p>
          </section>

          {/* Managing Information */}
          <section id="managing-information" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Managing Information</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              You are able to access, update, and delete certain Personal Information we have about you. You can update your profile information, change your email address, and modify your privacy settings at any time through the Settings page in your account.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              If you would like to permanently delete your account, you can do so from your Profile page. See the "Retention of Information" section below for details on what happens when you delete your account.
            </p>
          </section>

          {/* Disclosure of Information */}
          <section id="disclosure-of-information" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Disclosure of Information</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              Depending on the requested Services or as necessary to complete any transaction or provide any Service you have requested, we may share your non-personally identifiable information with our contracted companies and service providers (collectively, "Service Providers") we rely upon to assist in the operation of the Website and Services. These include:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li><strong className="text-white">Supabase:</strong> Database hosting and user authentication</li>
              <li><strong className="text-white">Vercel:</strong> Website hosting and content delivery</li>
              <li><strong className="text-white">PostHog:</strong> Product analytics (see "Analytics" section below)</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              Service Providers are not authorized to use or disclose your information except as necessary to perform services on our behalf or comply with legal requirements. We will not share any information with unaffiliated third parties.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              We may also disclose any Personal Information we collect, use or receive if required or permitted by law, such as to comply with a subpoena or similar legal process, and when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.
            </p>
          </section>

          {/* Retention of Information */}
          <section id="retention-of-information" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Retention of Information</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We will retain and use your Personal Information for as long as your user account remains active, to enforce our Policy, resolve disputes, and unless a longer retention period is required or permitted by law.
            </p>
            
            <div className="bg-dive-darker border border-dive-gray rounded-lg p-4 mb-4">
              <p className="text-white font-medium mb-2">Account Deletion (Hard Delete Policy)</p>
              <p className="text-gray-300 leading-relaxed">
                When you delete your account, we perform a <strong className="text-white">permanent, hard deletion</strong> of all your data from our database. This includes:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-300 mt-2">
                <li>Your profile information (username, display name, avatar, bio)</li>
                <li>All your bar ratings and reviews</li>
                <li>All photos you uploaded</li>
                <li>All comments you made</li>
                <li>All lists you created</li>
                <li>Your follower and following relationships</li>
                <li>Your notification history</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-2">
                This deletion is <strong className="text-white">immediate and irreversible</strong>. We do not retain copies or backups of your personal data after deletion.
              </p>
            </div>
            
            <p className="text-gray-300 leading-relaxed">
              We may use aggregated, anonymized data derived from your activity (such as overall rating statistics for a bar) after you delete your account, but this data cannot identify you personally.
            </p>
          </section>

          {/* Local Storage and Cookies */}
          <section id="local-storage-and-cookies" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Local Storage and Cookies</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              Tilted primarily uses <strong className="text-white">browser localStorage</strong> rather than traditional cookies to store information on your device. This provides a more privacy-friendly experience.
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">What we store in localStorage:</strong>
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li><strong className="text-white">Authentication session:</strong> Keeps you logged in between visits (via Supabase)</li>
              <li><strong className="text-white">Age verification:</strong> Remembers that you confirmed you are 21+</li>
              <li><strong className="text-white">App preferences:</strong> Your filter settings (e.g., "Dive Bars Only")</li>
              <li><strong className="text-white">Location permission state:</strong> Whether you've granted or denied location access</li>
              <li><strong className="text-white">Anonymous visitor ID:</strong> A randomly generated ID used for anonymous usage analytics (stored by both our first-party system and PostHog)</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">Essential cookies from our service providers:</strong>
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              Our hosting provider (Vercel) and authentication provider (Supabase) may set essential cookies strictly for security purposes, fraud prevention, and system functionality. These are not used for advertising or cross-site tracking.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              You can clear localStorage data through your browser settings. Note that doing so will log you out and reset your preferences.
            </p>
          </section>

          {/* Analytics */}
          <section id="first-party-analytics" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Analytics</h2>

            <p className="text-gray-300 leading-relaxed mb-4">
              We use analytics to monitor app performance and understand how users interact with Tilted. We use two systems:
            </p>

            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">1. First-party analytics (Supabase):</strong> A custom system hosted on our own infrastructure that collects basic page visit data (page paths, referrer URLs, browser user agent, and an anonymous visitor ID). This data is not shared with any third parties.
            </p>

            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">2. PostHog:</strong> A product analytics platform used to understand how features are used. PostHog collects:
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Feature usage events (e.g., rating a bar, searching, scanning a QR code)</li>
              <li>Page views</li>
              <li>Device type and browser information</li>
              <li>An anonymous identifier (linked to your account if you are logged in)</li>
            </ul>

            <p className="text-gray-300 leading-relaxed mb-4">
              PostHog data is processed on PostHog's US-based infrastructure. We do <strong className="text-white">not</strong> use PostHog's session recording feature. For more information, see{' '}
              <a href="https://posthog.com/privacy" className="text-dive-neon hover:underline" target="_blank" rel="noopener noreferrer">PostHog's Privacy Policy</a>.
            </p>

            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">What we do NOT use:</strong>
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Google Analytics</li>
              <li>Meta/Facebook Pixel</li>
              <li>Cross-site tracking cookies</li>
              <li>Advertising identifiers</li>
            </ul>

            <p className="text-gray-300 leading-relaxed">
              Analytics data is used solely to improve app performance, identify technical issues, and understand usage patterns. We do not sell or share this data with advertisers or data brokers.
            </p>
          </section>

          {/* Age Restriction */}
          <section id="age-restriction" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Age Restriction (21+)</h2>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-amber-400 font-medium">
                Tilted is strictly for users who are 21 years of age or older.
              </p>
            </div>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              Because Tilted is a platform focused on bars and alcohol-related venues, we require all users to be of legal drinking age in the United States (21 years or older).
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong className="text-white">How we enforce this:</strong>
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li><strong className="text-white">Age gate:</strong> First-time visitors must confirm they are 21 or older before accessing the app</li>
              <li><strong className="text-white">Birthdate verification at sign-up:</strong> When creating an account, users must provide their date of birth. Our system calculates your age and will reject account creation if you are under 21</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We do not knowingly collect any Personal Information from individuals under the age of 21. If you are under 21, please do not attempt to create an account or submit any Personal Information through Tilted.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              If you have reason to believe that someone under the age of 21 has created an account or provided Personal Information to us, please contact us immediately so we can delete that information.
            </p>
          </section>

          {/* Do Not Track Signals */}
          <section id="do-not-track-signals" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Do Not Track Signals</h2>
            
            <p className="text-gray-300 leading-relaxed">
              Tilted does not track users across third-party websites or services. Because we do not use third-party tracking scripts or advertising cookies, your browser's "Do Not Track" setting does not impact your experience on Tilted. We respect your privacy by default.
            </p>
          </section>

          {/* Links to Other Resources */}
          <section id="links-to-other-resources" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Links to Other Resources</h2>
            
            <p className="text-gray-300 leading-relaxed">
              The Website and Services may contain links to other resources that are not owned or controlled by us (such as bar websites, Google Maps, or social media platforms). Please be aware that we are not responsible for the privacy practices of such other resources or third parties. We encourage you to be aware when you leave Tilted and to read the privacy statements of each and every resource that may collect Personal Information.
            </p>
          </section>

          {/* Information Security */}
          <section id="information-security" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Information Security</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We take the security of your data seriously and implement industry-standard protections:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>All data is transmitted over HTTPS (encrypted connections)</li>
              <li>Passwords are securely hashed and never stored in plain text</li>
              <li>Database access is protected by Row Level Security policies</li>
              <li>Authentication is handled by Supabase, a trusted security provider</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              No data transmission over the Internet can be guaranteed 100% secure. While we strive to protect your Personal Information, you acknowledge that there are inherent security limitations of the Internet.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              Please take appropriate measures to protect your account, including using a strong, unique password and not sharing your login credentials.
            </p>
          </section>

          {/* Data Breach */}
          <section id="data-breach" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Data Breach</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              In the event we become aware that the security of the Website and Services has been compromised or Users' Personal Information has been disclosed to unrelated third parties as a result of external activity, including security attacks or fraud, we reserve the right to take reasonably appropriate measures, including investigation and reporting, as well as notification to and cooperation with law enforcement authorities.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              In the event of a data breach, we will make reasonable efforts to notify affected individuals if we believe that there is a reasonable risk of harm as a result of the breach or if notice is otherwise required by law. When we do, we will post a notice on the Website and send you an email. In jurisdictions where required, we may also report the breach to relevant authorities in accordance with applicable data protection regulations.
            </p>
          </section>

          {/* Changes and Amendments */}
          <section id="changes-and-amendments" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Changes and Amendments</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We reserve the right to modify this Policy or its terms related to the Website and Services at any time at our discretion. When we do, we will revise the "Last updated" date at the top of this page and post a notification on the main page of the Website. We may also provide notice to you in other ways at our discretion, such as through the contact information you have provided.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              An updated version of this Policy will be effective immediately upon the posting of the revised Policy unless otherwise specified. Your continued use of the Website and Services after the effective date of the revised Policy will constitute your consent to those changes. However, we will not, without your consent, use your Personal Information in a manner materially different than what was stated at the time your Personal Information was collected.
            </p>
          </section>

          {/* Acceptance of This Policy */}
          <section id="acceptance-of-this-policy" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Acceptance of This Policy</h2>
            
            <p className="text-gray-300 leading-relaxed">
              You acknowledge that you have read this Policy and agree to all its terms and conditions. By accessing and using the Website and Services and submitting your information you agree to be bound by this Policy. If you do not agree to abide by the terms of this Policy, you are not authorized to access or use the Website and Services.
            </p>
          </section>

          {/* Contacting Us */}
          <section id="contacting-us" className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">Contacting Us</h2>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions, concerns, or complaints regarding this Policy, the information we hold about you, or if you wish to exercise your rights, we encourage you to contact us:
            </p>
            
            <p className="mb-4">
              <a href="mailto:gettiltedapp@gmail.com" className="text-dive-neon hover:underline">gettiltedapp@gmail.com</a>
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-4">
              We will attempt to resolve complaints and disputes and make every reasonable effort to honor your wish to exercise your rights as quickly as possible and in any event, within the timescales provided by applicable data protection laws.
            </p>
            
            <p className="text-gray-300 leading-relaxed">
              If you believe your concerns have not been adequately addressed, you may escalate the matter to the appropriate data protection authority in your region, in accordance with applicable privacy laws.
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-dive-gray pt-6 mt-8">
            <p className="text-gray-500 text-sm text-center">
              This document was last updated on March 16, 2026
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
